import { PublicClientApplication, NavigationClient } from '@azure/msal-browser';

class NoRedirectNavigationClient extends NavigationClient {
    async navigateExternal(_url, _options) {
        return false;
    }
}

export class EntraProvider {

    initialized = false;

    redirectResponse = null;

    constructor(authConfig) {
        const clientOrigin = window.location.origin;
        const tenantIdMatch = authConfig.domain?.match(/\/([0-9a-f-]{36}|[^\/]+)$/i);
        this.tenantId = tenantIdMatch ? tenantIdMatch[1] : null;

        // Configure MSAL with settings from backend
        const msalConfig = {
            auth: {
                clientId: authConfig.clientId,
                authority: authConfig.domain,
                redirectUri: `${clientOrigin}/callback`,
                postLogoutRedirectUri: `https://login.microsoftonline.com/${this.tenantId}/oauth2/v2.0/logout`,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false
            }
        };

        this.msalInstance = new PublicClientApplication(msalConfig);
        this.loginRequest = {
            scopes: authConfig.userScopes
        };
    }

    initialize = async () => {
        if(this.initialized) {
            return;
        }

        await this.msalInstance.initialize();
        this.initialized = true;

        this.redirectResponse = await this.msalInstance.handleRedirectPromise();

        if(this.redirectResponse) {
            this.msalInstance.setActiveAccount(this.redirectResponse.account);
            return;
        }

        const account = this.getActiveAccount();
        if (account) {
            await this.msalInstance.acquireTokenSilent({
                scopes: this.loginRequest.scopes,
                account
            }).then(response => {
                console.debug('Token acquired silently:', response);
                this.msalInstance.setActiveAccount(response.account);
            });
        }
    };

    login = async (_language) => {
        await this.initialize();
        await this.msalInstance.loginRedirect({
            scopes: this.loginRequest.scopes
        });
    }

    silentAuthentication = async (callback) => {        
        await this.initialize();
        this.msalInstance.acquireTokenSilent({
            ...this.loginRequest,
            account: this.getActiveAccount()
        });
    }

    otcAuthentication = (_code, _userName) => {
        return Promise.reject(new Error('OTC authentication is not supported with Entra Authentication'));
    };

    passUserName = (_userName) => {
        return Promise.reject(new Error('passUserName is not supported with Entra Authentication'));
    };

    logout = async () => {
        await this.initialize();
        const account = this.getActiveAccount();

        if (account) {
            this.msalInstance.logoutRedirect({ account })
            .catch(error => {
                console.error('Logout failed:', error);
            });
        }
    }

    handleAuthentication = async () => {
        // Wait for initialization to complete
        await this.initialize();


        // No redirect response - check if there's an authenticated account in cache
        // This handles cases like "Use a different account" where redirectPromise returns null
        const account = this.getActiveAccount();
        if (account) {
            return {};
        }

        return {};
    }

    getAccessToken = async () => {
        const account = this.getActiveAccount();

        if (!account) {
            throw new Error('No authenticated account found');
        }

        const tokenRequest = {
            account: account,
            scopes: this.loginRequest.scopes
        };

        const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
        return response.accessToken; 
    }

    getProfile = () => {
        try {
            const account = this.getActiveAccount();

            // Map MSAL account to the profile structure expected by the app
            return {
                givenName: account.idTokenClaims?.given_name || account.name?.split(' ')[0] || '',
                employeeId: account.idTokenClaims?.employeeId || account.idTokenClaims?.oid || ''
            };
        } catch (error) {
            console.debug('getProfile called before MSAL initialized, returning empty');
            return {};
        }
    }

    getTokenTimeToLive = () => {
        try {
            const account = this.getActiveAccount();

            if (!account) {
                return 0;
            }

            if (account.idTokenClaims && account.idTokenClaims.exp) {
                const expiresAtNumber = account.idTokenClaims.exp * 1000;
                const now = new Date().getTime();
                const ttl = expiresAtNumber - now;
                return ttl > 0 ? ttl : 0;
            }

            return 0;
        } catch (error) {
            console.error('Error getting token TTL:', error);
            return 0;
        }
    }

    isAuthenticated = () => this.getActiveAccount() !== null;

    isRegularAuthentication = (_authenticationType) => true;

    getActiveAccount = () => {
        const activeAccount = this.msalInstance.getActiveAccount();
        if (activeAccount) return activeAccount;

        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            this.msalInstance.setActiveAccount(accounts[0]);
            return accounts[0];
        }
        return null;
    };
}

export default EntraProvider;
